import React, { useEffect, useRef, useState, useCallback } from "react";
import { Typography, Box, Stack, Button, CircularProgress } from "@mui/material";
import { ReactComponent as DoneIcon } from "../../../../vendor/_new/icon/sites/done.svg";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { ReactComponent as ArrowleftIcon } from "../../../../vendor/_new/icon/sites/arrow_left.svg";
import colors from "../../../../utils/colors";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useWAFConfig from "../../../../hooks/user/useWAFConfig";
import { CertificateType, SslType } from "../../../../utils/constants";
import useSite from "../../../../hooks/user/useSite";
import { containedButtonSx, outlinedButtonSx } from "../../../common/Button";

const configurationProgressStrings = [
  "Register site in Layerd AI",
  "Generate / Upload SSL certificates",
  "Install SSL certificates",
  "Enable HTTP to HTTPS redirection",
  "Enable Layerd Signature WAF",
  "Final Review",
];

const StatusValue = { PENDING: -1, SKIPPED: 0, DONE: 1, ERROR: 2, PROCESSING: 3 };

const ConfigurationProcess = ({ handleNext, handlePrev }) => {
  const { sslConfig, getSSLConfig } = useSSLConfig();
  const { wafConfig, getWAFConfig } = useWAFConfig();
  const { cursite } = useSite();
  const [siteUid, setSiteUid] = useState();
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const [timerTick, setTimerTick] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cursite) {
      setSiteUid(cursite.id);
    }
  }, [cursite]);

  useEffect(() => {
    if (!siteUid) return;
    if (!sslConfig) {
      getSSLConfig(siteUid);
    }
    if (!wafConfig) {
      getWAFConfig(siteUid);
    }
  }, [sslConfig, wafConfig, getSSLConfig, getWAFConfig, siteUid]);

  const updateProgress = useCallback(async () => {
    const oldProgress = progress;
    let newProgress = 0;
    setTimerTick((oldTick) => oldTick + 1);
    switch (oldProgress) {
      case 0:
        newProgress = 1;
        break;
      case 1:
        if (SslType.OFF === sslConfig.ssl_type || CertificateType.SENSE_GUARD === sslConfig.certs?.type || sslConfig.certs?.host?.length) {
          newProgress = 2;
        } else if (SslType.OFF < sslConfig.ssl_type && CertificateType.CUSTOM === sslConfig.certs?.type && !sslConfig.certs?.host?.length) {
          newProgress = 2;
          clearTimeout(timer.current);
          setError(true);
        } else {
          if (0 === timerTick % 10) {
            getSSLConfig(siteUid);
          }
          newProgress = 1;
        }
        break;
      case 2:
        if (SslType.OFF === sslConfig.ssl_type || sslConfig.certs?.host?.length) {
          newProgress = 3;
        } else {
          if (0 === timerTick % 10) {
            getSSLConfig(siteUid);
          }
          newProgress = 2;
        }
        break;
      case 3:
        newProgress = 4;
        break;
      case 4:
        newProgress = 5;
        break;
      case 5:
        if (timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
        newProgress = 6;
        break;
      default:
        newProgress = 6;
        break;
    }
    setProgress(newProgress);
  }, [progress, sslConfig, siteUid, getSSLConfig, timerTick]);

  React.useEffect(() => {
    if (wafConfig && sslConfig && null === timer.current) {
      timer.current = setInterval(() => {
        updateProgress();
      }, 500);
    }
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [wafConfig, sslConfig, updateProgress]);

  const getStepStatus = (step) => {
    if (step < progress) {
      switch (step) {
        case 1:
          return SslType.OFF === sslConfig?.ssl_type
            ? StatusValue.SKIPPED
            : sslConfig?.certs?.host?.length || CertificateType.SENSE_GUARD === sslConfig.certs?.type
            ? StatusValue.DONE
            : StatusValue.ERROR;
        case 2:
          return SslType.OFF < sslConfig?.ssl_type
            ? sslConfig?.certs?.host?.length
              ? StatusValue.DONE
              : StatusValue.ERROR
            : StatusValue.SKIPPED;
        case 3:
          return SslType.OFF < sslConfig?.ssl_type && true === sslConfig?.https_redirect_enabled
            ? StatusValue.DONE
            : StatusValue.SKIPPED;
        case 4:
          return true === wafConfig?.sd_sig_module_active ? StatusValue.DONE : StatusValue.SKIPPED;
        default:
          return StatusValue.DONE;
      }
    } else if (step === progress && !error) {
      return StatusValue.PROCESSING;
    }
    return StatusValue.PENDING;
  };

  const progressPercent = Math.round((progress / configurationProgressStrings.length) * 100);
  const isComplete = progress >= configurationProgressStrings.length;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pb: 4 }}>
        Configuration process for your application
      </Typography>

      <Stack direction="row" spacing={10} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            {configurationProgressStrings.map((stepText, index) => {
              const status = getStepStatus(index);
              const isDone = status === StatusValue.DONE;
              const isSkipped = status === StatusValue.SKIPPED;
              const isProcessing = status === StatusValue.PROCESSING;
              const isError = status === StatusValue.ERROR;
              const isPending = status === StatusValue.PENDING;

              let borderColor = colors.neutral[200];
              if (isDone) borderColor = colors.green[200];
              if (isProcessing) borderColor = colors.primary[200];
              if (isError) borderColor = colors.red[100];

              return (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: isProcessing ? colors.primary[300] : isError ? colors.red[100] : "transparent",
                      border: (isDone || isSkipped || isPending) ? `2px solid ${isDone ? colors.green[300] : colors.neutral[400]}` : "none",
                      flexShrink: 0,
                    }}
                  >
                    {isDone ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke={colors.green[300]} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : isProcessing ? (
                      <CircularProgress size={14} sx={{ color: "#fff" }} />
                    ) : isError ? (
                      <Typography sx={ { fontSize: 12, fontWeight: "bold", color: "#fff" }}>!</Typography>
                    ) : (
                      <Typography sx={ { fontSize: 12, fontWeight: "medium", color: colors.neutral[500] }}>{index + 1}</Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: "21px",
                      fontWeight: (isDone || isProcessing) ? "medium" : "regular",
                      color: isDone ? colors.green[300] : isProcessing ? colors.primary[300] : isError ? colors.red[100] : colors.neutral[600],
                      flex: 1,
                    }}
                  >
                    {stepText}
                  </Typography>
                  <Typography sx={ { fontSize: 12, color: isDone ? colors.green[300] : isError ? colors.red[100] : isProcessing ? colors.primary[300] : colors.neutral[500] }}>
                    {isDone ? "Done" : isSkipped ? "Skipped" : isError ? "Error" : isProcessing ? "In Progress" : ""}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Box
          sx={{
            width: 240,
            height: 240,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={220}
            thickness={4}
            sx={{ color: colors.neutral[200], position: "absolute" }}
          />
          <CircularProgress
            variant="determinate"
            value={progressPercent}
            size={220}
            thickness={4}
            sx={{
              color: isComplete ? colors.green[300] : colors.primary[300],
              position: "absolute",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isComplete ? (
              <DoneIcon style={{ width: 48, height: 48, color: colors.green[300] }} />
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: 40,
                    lineHeight: "48px",
                    fontWeight: "bold",
                    color: colors.primary[300],
                  }}
                >
                  {progressPercent}%
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "18px",
                    color: colors.neutral[600],
                  }}
                >
                  Deploying
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={4} sx={{ pt: 4 }}>
        {error && (
          <Button
            startIcon={<ArrowleftIcon style={{ color: "#6371F7" }} />}
            onClick={handlePrev}
            sx={{ height: 36, ...outlinedButtonSx }}
          >
            Back
          </Button>
        )}
        <Button
          endIcon={<ArrowrightIcon style={{ color: "#fff" }} />}
          onClick={handleNext}
          sx={{ height: 36, ...containedButtonSx }}
        >
          Continue
        </Button>
      </Stack>
    </Box>
  );
};

export default ConfigurationProcess;
